import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useToast } from '../../components/ui/Toast';
import { ErrorNotice, Loading } from '../../components/ui';
import { DateInput, Drawer, Field, Select, TextInput } from '../../components/ui/Form';
import { date, money, today, weight } from '../../utils/format';

interface Line {
  line_id: string;
  item_code: string | null;
  product_name: string;
  purity_code: string | null;
  net_wt: string;
  line_total: string;
  returned_on: string | null;
  returned_id: string | null;
}

interface SaleDetail {
  id: string;
  doc_no: string;
  doc_date: string;
  branch_id: string;
  customer_id: string | null;
  customer_name: string | null;
  walkin_name: string | null;
  lines: Line[];
}

/**
 * Taking back some or all of the pieces on a bill. Each chosen piece goes back
 * on the shelf (or is kept aside as damaged), its sale and GST come off the
 * books at what they were billed at, and the money goes back the way the
 * customer wants it: cash, the bank, or a credit note against their account.
 */
export function SalesReturnDrawer({ saleId, onClose }: { saleId: string; onClose: () => void }) {
  const client = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [chosen, setChosen] = useState<Record<string, { restock: boolean; condition: string }>>({});
  const [reason, setReason] = useState('');
  const [refundMode, setRefundMode] = useState<'CASH' | 'BANK' | 'CREDIT' | ''>('');
  const [docDate, setDocDate] = useState(today());
  // One key per drawer: a double click posts one credit note, not two.
  const [key] = useState(() => crypto.randomUUID());

  const sale = useQuery({
    queryKey: ['sale', saleId],
    queryFn: async () => (await endpoints.sales.get(saleId)) as unknown as SaleDetail,
  });

  const walkIn = sale.data ? !sale.data.customer_id : false;
  const mode = refundMode || (walkIn ? 'CASH' : 'CREDIT');
  const open = (sale.data?.lines ?? []).filter((l) => !l.returned_on);
  const done = (sale.data?.lines ?? []).filter((l) => l.returned_on);
  const ids = Object.keys(chosen);
  const refund = useMemo(
    () => open.filter((l) => chosen[l.line_id]).reduce((a, l) => a + Number(l.line_total), 0),
    [open, chosen],
  );

  const post = useMutation({
    mutationFn: () =>
      api.post<{ returnId: string; docNo: string; grandTotal: string }>(
        '/sales-returns',
        {
          branchId: sale.data!.branch_id,
          docDate,
          saleId,
          reason: reason.trim(),
          refundMode: mode,
          lines: ids.map((id) => ({
            saleLineId: id,
            restock: chosen[id]!.restock,
            ...(chosen[id]!.condition.trim() ? { condition: chosen[id]!.condition.trim() } : {}),
          })),
        },
        { 'idempotency-key': key },
      ),
    onSuccess: (r) => {
      toast(
        `${r.docNo} — ${money(r.grandTotal)} ${mode === 'CREDIT' ? 'credited to the customer' : mode === 'BANK' ? 'to refund by bank' : 'to refund in cash'}`,
        'The pieces taken back are in stock again unless marked damaged.',
      );
      void client.invalidateQueries({ queryKey: ['sales'] });
      void client.invalidateQueries({ queryKey: ['sale', saleId] });
      void client.invalidateQueries({ queryKey: ['items'] });
      onClose();
      navigate(`/sales-returns/${r.returnId}/print`);
    },
  });

  const toggle = (id: string): void =>
    setChosen((c) => {
      const next = { ...c };
      if (next[id]) delete next[id];
      else next[id] = { restock: true, condition: '' };
      return next;
    });

  const ready = ids.length > 0 && reason.trim().length >= 3 && !post.isPending;

  return (
    <Drawer
      wide
      title={sale.data ? `Return against ${sale.data.doc_no}` : 'Sales return'}
      subtitle={
        sale.data
          ? `Billed ${date(sale.data.doc_date)} to ${sale.data.customer_name ?? sale.data.walkin_name ?? 'a walk-in customer'}`
          : undefined
      }
      onClose={onClose}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm">
            {ids.length > 0 ? (
              <>
                {ids.length} {ids.length === 1 ? 'piece' : 'pieces'} · refund{' '}
                <span className="num font-semibold">{money(refund.toFixed(2))}</span>
              </>
            ) : (
              <span className="text-ink-soft">Tick the pieces coming back.</span>
            )}
          </span>
          <button className="btn-primary" disabled={!ready} onClick={() => post.mutate()}>
            {post.isPending ? 'Posting…' : 'Post the return'}
          </button>
        </div>
      }
    >
      {sale.isLoading ? <Loading /> : null}
      <ErrorNotice error={sale.error ?? post.error} />
      {sale.data ? (
        <div className="space-y-4">
          {open.length === 0 ? (
            <p className="text-sm text-ink-soft">Every piece on this bill has already been returned.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="th w-8" />
                    <th className="th text-left">Piece</th>
                    <th className="th text-right">Net wt</th>
                    <th className="th text-right">Billed at</th>
                    <th className="th text-left">Back on the shelf?</th>
                  </tr>
                </thead>
                <tbody>
                  {open.map((l) => {
                    const c = chosen[l.line_id];
                    return (
                      <tr key={l.line_id} className={c ? 'bg-gold-50/40' : ''}>
                        <td className="td">
                          <input
                            type="checkbox"
                            aria-label={`Return ${l.item_code ?? l.product_name}`}
                            checked={!!c}
                            onChange={() => toggle(l.line_id)}
                          />
                        </td>
                        <td className="td">
                          <div className="num">{l.item_code ?? '—'}</div>
                          <div className="text-xs text-ink-soft">
                            {l.product_name}
                            {l.purity_code ? ` · ${l.purity_code}` : ''}
                          </div>
                        </td>
                        <td className="td num text-right">{weight(l.net_wt)}</td>
                        <td className="td num text-right">{money(l.line_total)}</td>
                        <td className="td">
                          {c ? (
                            <div className="flex min-w-[14rem] flex-col gap-1">
                              <select
                                className="field"
                                value={c.restock ? 'yes' : 'no'}
                                onChange={(e) =>
                                  setChosen((x) => ({
                                    ...x,
                                    [l.line_id]: { ...c, restock: e.target.value === 'yes' },
                                  }))
                                }
                              >
                                <option value="yes">Yes — fit to sell again</option>
                                <option value="no">No — damaged, keep aside</option>
                              </select>
                              {!c.restock ? (
                                <input
                                  className="field"
                                  placeholder="What is wrong with it"
                                  value={c.condition}
                                  maxLength={200}
                                  onChange={(e) =>
                                    setChosen((x) => ({ ...x, [l.line_id]: { ...c, condition: e.target.value } }))
                                  }
                                />
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-ink-faint">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {done.length > 0 ? (
            <p className="text-xs text-ink-soft">
              Already returned:{' '}
              {[...new Map(done.map((l) => [l.returned_id, l])).values()].map((l, i) => (
                <span key={l.returned_id ?? i}>
                  {i > 0 ? ', ' : ''}
                  <Link className="underline" to={`/sales-returns/${l.returned_id}/print`}>
                    {l.returned_on}
                  </Link>{' '}
                  (
                  {done
                    .filter((d) => d.returned_id === l.returned_id)
                    .map((d) => d.item_code ?? d.product_name)
                    .join(', ')}
                  )
                </span>
              ))}
              .
            </p>
          ) : null}

          {open.length > 0 ? (
            <div className="grid gap-x-4 gap-y-3 sm:grid-cols-3">
              <Field label="Return date" required>
                <DateInput value={docDate} onChange={setDocDate} min={sale.data.doc_date.slice(0, 10)} />
              </Field>
              <Field
                label="Refund"
                required
                hint={walkIn ? 'A walk-in bill has no account to credit, so it is paid back.' : undefined}
              >
                <Select
                  value={mode}
                  onChange={(v) => setRefundMode(v as 'CASH' | 'BANK' | 'CREDIT')}
                  options={[
                    ...(walkIn ? [] : [{ value: 'CREDIT', label: 'Credit note to the customer' }]),
                    { value: 'CASH', label: 'Cash back' },
                    { value: 'BANK', label: 'Back to the bank / UPI' },
                  ]}
                />
              </Field>
              <Field label="Reason" required hint="Kept on the credit note.">
                <TextInput value={reason} onChange={setReason} placeholder="Size did not fit" maxLength={500} />
              </Field>
            </div>
          ) : null}
        </div>
      ) : null}
    </Drawer>
  );
}
